import { useMemo } from 'react'

import { IconSparkleFill } from '@vtex/shoreline'
import { t } from 'i18next'

import Ring from '~/components/Ring'
import Tooltip from '~/components/Tooltip'
import { formatMoneyV2, isAdvertiser as isAdvertiserFunction } from '~/helpers'
import { MetricMethodologyTooltip } from '~/modules/retailMedia/components/MetricMethodologyTooltip'
import { useCampaignReadOnly } from '~/modules/retailMedia/hooks/useCampaignReadOnly'

import * as S from './styles'

const FILL_GREEN = '#0ACB55'

function getSuggestionTooltipKey(
  insideCampaign: boolean,
  canClick: boolean
): string {
  if (insideCampaign) {
    if (canClick) {
      return 'rm:metrics.bidStrength.suggestedValue.tooltip'
    }
    return 'rm:metrics.bidStrength.suggestedValue.tooltip.notActionable'
  }
  return 'rm:metrics.bidStrength.suggestedValue.tooltip.viewOnly'
}

const TIER_RING: Record<
  BidCostCompetitiveness,
  { percentage: number; color: string }
> = {
  low: { percentage: 25, color: 'var(--sl-color-red-8)' },
  below_average: { percentage: 50, color: 'var(--sl-color-orange-7)' },
  above_average: { percentage: 75, color: FILL_GREEN },
  high: { percentage: 100, color: FILL_GREEN }
}

/**
 * Anel de competitividade + tooltip (título + descrição, mesmo padrão dos
 * tooltips de métrica). Renderizado ao lado do valor de CPC/CPM — ver cost.tsx.
 */
const BidStrength: RowBidStrengthColumnData = ({ status, costType }) => {
  if (!status) return null

  const costTypeLabel = costType.toUpperCase()
  // Chave dinâmica: o `t` tipado não aceita template string (mesmo padrão do
  // MetricTooltip)
  const translate = t as unknown as (
    key: string,
    opts?: Record<string, unknown>
  ) => string
  const tooltipTitle = translate(`rm:metrics.bidStrength.tooltip.${status}`)
  const tooltipDescription = translate(
    `rm:metrics.bidStrength.tooltip.${status}.description`,
    { costType: costTypeLabel }
  )

  const ring = TIER_RING[status]

  return (
    <Tooltip
      title={
        <S.TooltipTitle>
          <Ring
            percentage={ring.percentage}
            color={ring.color}
            data-status={status}
          />
          {tooltipTitle}
        </S.TooltipTitle>
      }
      text={<MetricMethodologyTooltip description={tooltipDescription} />}
      interactiveTrigger
    >
      <Ring
        percentage={ring.percentage}
        color={ring.color}
        accessibleLabel={tooltipTitle}
        data-status={status}
        // Ajuste fino: dígitos não têm descendente, então o centro óptico do
        // valor fica acima do centro geométrico da linha — sem isso o anel
        // (simétrico na própria caixa) parece "baixo" ao lado do valor.
        style={{ marginTop: '-1px' }}
      />
    </Tooltip>
  )
}

/**
 * Sugestão de lance — renderizado abaixo do valor de CPC/CPM. Clicar abre o
 * mesmo formulário de edição da coluna (ver cost.tsx), pré-preenchido e com
 * origin bid_monitor no PATCH.
 */
export const BidStrengthSuggestion: RowBidStrengthSuggestionData = ({
  suggestion,
  onStartEdit,
  canApply = true,
  insideCampaign = false
}) => {
  const isAdvertiser = isAdvertiserFunction()
  const isReadOnlyView = useCampaignReadOnly()

  const formattedSuggestion = useMemo(
    () => formatMoneyV2({ value: suggestion }),
    [suggestion]
  )

  if (suggestion === null || suggestion === undefined) return null

  const chip = (
    <>
      <div className="icon">
        <IconSparkleFill width={12} height={12} />
      </div>
      {formattedSuggestion}
    </>
  )

  const canClick = isAdvertiser && canApply && !isReadOnlyView
  const suggestionDisabled = canClick === false
  const suggestionTooltipKey = getSuggestionTooltipKey(insideCampaign, canClick)

  const handleSuggestionClick = () => {
    if (canClick) {
      onStartEdit(Number(suggestion))
    }
  }

  return (
    <S.Container className="BidStrengthSuggestion">
      <Tooltip noMargin interactiveTrigger text={t(suggestionTooltipKey)}>
        <S.SuggestionButton
          type="button"
          className="suggestion"
          disabled={suggestionDisabled}
          onClick={handleSuggestionClick}
        >
          {chip}
        </S.SuggestionButton>
      </Tooltip>
    </S.Container>
  )
}

export default BidStrength
